import { useUnit } from 'effector-react';
import {
  Check,
  CircleAlert,
  Cloud,
  CloudOff,
  Eye,
  HardDrive,
  Loader2,
  PanelLeft,
  PanelRight,
  Redo2,
  Search,
  Undo2,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $hasOverrides, visibilityReset } from '../../entities/item-visibility';
import { $openSync, $saveStatus } from '../../entities/library';
import {
  $canRedo,
  $canUndo,
  $project,
  $readOnly,
  commandIssued,
  redo,
  undo,
} from '../../entities/project';
import {
  $isSimulating,
  $mcError,
  $mcProgress,
  $mcResult,
  $result,
  $simError,
  mcCancelled,
} from '../../entities/simulation';
import {
  $simMode,
  $valueMode,
  simModeChanged,
  valueModeChanged,
} from '../../entities/view-settings';
import { AccountMenu } from '../../features/account';
import { paletteOpened } from '../command-palette';
import { PreferencesMenu } from '../../features/preferences-menu';
import { SaveCopyButton } from '../../features/save-copy';
import { ShareButton } from '../../features/share-project';
import { HelpMenu } from '../help-center';
import { ReportButton } from '../report';
import { cn } from '../../shared/lib/cn';
import { formatNumber, formatPercent } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { routes } from '../../shared/routing';
import { CommitField, IconButton, Segmented, Shortcut, Tooltip } from '../../shared/ui';

export interface TopBarProps {
  onToggleVariants: () => void;
  onToggleInspector: () => void;
  /** Small screens: a read-only viewer (no editing controls; mode switches on a second row). */
  compact?: boolean;
}

/** Nominal/real and deterministic/Monte Carlo. */
function ModeSwitches() {
  const { t } = useTranslation();
  const [valueMode, simMode, onValueMode, onSimMode] = useUnit([
    $valueMode,
    $simMode,
    valueModeChanged,
    simModeChanged,
  ]);
  return (
    <>
      <Segmented
        label={t('valueMode.label')}
        value={valueMode}
        onValueChange={onValueMode}
        options={[
          {
            value: 'nominal',
            label: t('valueMode.nominal'),
            hint: t('valueMode.nominalHint'),
            shortcut: ['R'],
          },
          {
            value: 'real',
            label: t('valueMode.real'),
            hint: t('valueMode.realHint'),
            shortcut: ['R'],
          },
        ]}
      />
      <Segmented
        label={t('simMode.label')}
        value={simMode}
        onValueChange={onSimMode}
        options={[
          {
            value: 'deterministic',
            label: t('simMode.deterministic'),
            hint: t('simMode.deterministicHint'),
            shortcut: ['⇧', 'M'],
          },
          {
            value: 'monteCarlo',
            label: t('simMode.monteCarlo'),
            hint: t('simMode.monteCarloHint'),
            shortcut: ['⇧', 'M'],
          },
        ]}
      />
    </>
  );
}

export function TopBar({ onToggleVariants, onToggleInspector, compact = false }: TopBarProps) {
  const { t } = useTranslation();
  const [project, readOnly, issue, canUndo, canRedo, onUndo, onRedo, goHome] = useUnit([
    $project,
    $readOnly,
    commandIssued,
    $canUndo,
    $canRedo,
    undo,
    redo,
    routes.home.open,
  ]);

  if (compact) {
    return (
      <header className="shrink-0 border-b border-border-subtle bg-bg-1">
        <div className="flex h-11 items-center gap-1 px-2">
          <IconButton label={t('panels.toggleVariants')} onClick={onToggleVariants}>
            <PanelLeft />
          </IconButton>
          <button
            type="button"
            onClick={() => {
              void goHome();
            }}
            className="flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
          >
            <img src="/favicon.svg" alt={t('app.projects')} className="size-5" />
            <span className="truncate font-medium">{project?.name}</span>
          </button>
          <div className="flex-1" />
          <ReportButton compact />
          <PaletteButton />
          <HelpMenu />
          <PreferencesMenu />
          <AccountMenu />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto px-2 pb-2">
          <ModeSwitches />
          {readOnly && (
            <>
              <div className="flex-1" />
              <SaveCopyButton compact />
            </>
          )}
        </div>
      </header>
    );
  }

  return (
    <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border-subtle bg-bg-1 px-2">
      {/* The project name takes the free space; the palette button's text gives way first. */}
      <div className="flex min-w-0 flex-auto items-center gap-1">
        <Tooltip content={t('panels.toggleVariants')} shortcut={['[']}>
          <IconButton label={t('panels.toggleVariants')} onClick={onToggleVariants}>
            <PanelLeft />
          </IconButton>
        </Tooltip>
        <button
          type="button"
          onClick={() => {
            void goHome();
          }}
          className="flex shrink-0 items-center gap-2 rounded-md px-1 py-0.5 outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
        >
          <img src="/favicon.svg" alt="" className="size-5" />
          <span className="text-text-3">{t('app.projects')}</span>
        </button>
        <span className="shrink-0 text-text-4" aria-hidden>
          /
        </span>
        {project && readOnly && (
          <>
            <h1 className="min-w-0 max-w-md truncate px-1.5 font-medium">{project.name}</h1>
            <ViewOnlyBadge />
          </>
        )}
        {project && !readOnly && (
          <CommitField
            aria-label={t('app.renameProject')}
            // As wide as the name (field-sizing), up to the space left; long names end in "…".
            className="max-w-md min-w-12 [&_input]:w-auto [&_input]:max-w-full [&_input]:min-w-12 [&_input]:border-transparent [&_input]:bg-transparent [&_input]:px-1.5 [&_input]:font-medium [&_input]:text-ellipsis [&_input]:[field-sizing:content] [&_input]:hover:border-border-strong"
            value={project.name}
            format={(v) => v}
            parse={(text) =>
              text.trim()
                ? { ok: true, value: text.trim().slice(0, 120) }
                : { ok: false, error: t('fields.required') }
            }
            onCommit={(name) => {
              issue({ command: { type: 'project/rename', name } });
            }}
          />
        )}
      </div>

      <WhatIf />
      {!readOnly && <SaveStatus />}
      {!readOnly && <ShareButton />}
      <ReportButton />
      <PaletteButton />

      <div className="flex items-center gap-2">
        <SimStatus />
        {readOnly ? (
          <SaveCopyButton />
        ) : (
          <div className="flex items-center">
            <Tooltip content={t('history.undo')} shortcut={['⌘', 'Z']}>
              <IconButton label={t('history.undo')} disabled={!canUndo} onClick={onUndo}>
                <Undo2 />
              </IconButton>
            </Tooltip>
            <Tooltip content={t('history.redo')} shortcut={['⇧', '⌘', 'Z']}>
              <IconButton label={t('history.redo')} disabled={!canRedo} onClick={onRedo}>
                <Redo2 />
              </IconButton>
            </Tooltip>
          </div>
        )}
        <ModeSwitches />
        <Tooltip content={t('panels.toggleInspector')} shortcut={[']']}>
          <IconButton label={t('panels.toggleInspector')} onClick={onToggleInspector}>
            <PanelRight />
          </IconButton>
        </Tooltip>
        <HelpMenu />
        <PreferencesMenu />
        <AccountMenu />
      </div>
    </header>
  );
}

/** A shared project: nothing here changes it. */
function ViewOnlyBadge() {
  const { t } = useTranslation();
  return (
    <Tooltip content={t('viewer.viewOnlyHint')}>
      <span className="ml-1 flex items-center gap-1 rounded-full border border-border-subtle px-2 py-0.5 text-xs whitespace-nowrap text-text-2">
        <Eye className="size-3.5" aria-hidden />
        {t('viewer.viewOnly')}
      </span>
    </Tooltip>
  );
}

/** Signed in: where the project stands with the account; otherwise the local save status. */
function SaveStatus() {
  const sync = useUnit($openSync);
  return sync === 'none' ? <LocalSaveStatus /> : <SyncStatus />;
}

function SyncStatus() {
  const { t } = useTranslation();
  const sync = useUnit($openSync);
  const view = {
    synced: { icon: <Cloud />, label: t('sync.synced'), hint: t('sync.syncedHint') },
    pending: {
      icon: <Loader2 className="animate-spin" />,
      label: t('sync.pending'),
      hint: t('sync.syncedHint'),
    },
    syncing: {
      icon: <Loader2 className="animate-spin" />,
      label: t('sync.syncing'),
      hint: t('sync.syncedHint'),
    },
    offline: { icon: <CloudOff />, label: t('sync.offline'), hint: t('sync.offlineHint') },
    conflict: {
      icon: <CircleAlert className="text-negative" />,
      label: t('sync.conflict'),
      hint: t('sync.conflictTitle'),
    },
    localOnly: { icon: <HardDrive />, label: t('sync.localOnly'), hint: t('sync.localOnlyHint') },
    none: null,
  }[sync];
  if (!view) return null;
  return (
    <Tooltip content={view.hint}>
      <span
        role="status"
        className="flex items-center gap-1 text-xs whitespace-nowrap text-text-3 [&_svg]:size-3.5"
      >
        {view.icon}
        <span className={STATUS_TEXT}>{view.label}</span>
      </span>
    </Tooltip>
  );
}

/** Below 1280 px the save status is its icon (with the tooltip); the text stays for screen readers. */
const STATUS_TEXT = 'sr-only xl:not-sr-only';

function LocalSaveStatus() {
  const { t } = useTranslation();
  const status = useUnit($saveStatus);
  const icon =
    status === 'saving' ? (
      <Loader2 className="animate-spin" />
    ) : status === 'error' ? (
      <CircleAlert className="text-negative" />
    ) : status === 'saved' ? (
      <Check />
    ) : null;
  return (
    <Tooltip content={status === 'error' ? t('status.errorHint') : t('status.local')}>
      <span
        role="status"
        className={cn(
          'flex items-center gap-1 text-xs whitespace-nowrap text-text-3 [&_svg]:size-3.5',
          status === 'error' && 'text-negative',
        )}
      >
        {icon}
        <span className={STATUS_TEXT}>{t(`status.${status}`)}</span>
      </span>
    </Tooltip>
  );
}

function SimStatus() {
  const simMode = useUnit($simMode);
  return simMode === 'monteCarlo' ? <McStatus /> : <DeterministicStatus />;
}

/** Monte Carlo progress (with cancel) while running, then the paths and time of the last run. */
function McStatus() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [progress, result, error, cancel] = useUnit([
    $mcProgress,
    $mcResult,
    $mcError,
    mcCancelled,
  ]);
  if (progress !== null) {
    const percent = formatPercent(progress, locale, 0);
    return (
      <span className="flex items-center gap-1.5 text-xs whitespace-nowrap text-text-2">
        <span
          role="progressbar"
          aria-label={t('mc.runningLabel')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
          className="h-1 w-16 overflow-hidden rounded-full bg-bg-4"
        >
          <span
            className="block h-full rounded-full bg-accent transition-[width] duration-100"
            style={{ width: `${String(progress * 100)}%` }}
          />
        </span>
        <span className="tabular">{t('mc.running', { percent })}</span>
        <Tooltip content={t('mc.cancel')}>
          <IconButton size="sm" label={t('mc.cancel')} onClick={cancel}>
            <X />
          </IconButton>
        </Tooltip>
      </span>
    );
  }
  if (error) return <span className="text-xs text-negative">{t('mc.error')}</span>;
  if (!result) return null;
  const seconds =
    (result.timings.compileMs + result.timings.runMs + result.timings.aggregateMs) / 1000;
  return (
    <Tooltip content={t('mc.doneHint')}>
      {/* Timings are secondary: from 1280 px, so narrower bars keep room for the project name. */}
      <span className="tabular hidden text-xs whitespace-nowrap text-text-3 xl:inline">
        {t('mc.done', {
          paths: formatNumber(result.paths, locale),
          seconds: formatNumber(seconds, locale, 1),
        })}
      </span>
    </Tooltip>
  );
}

function DeterministicStatus() {
  const { t } = useTranslation();
  const [result, simulating, error] = useUnit([$result, $isSimulating, $simError]);
  if (error) {
    return <span className="text-xs text-negative">{t('status.simError')}</span>;
  }
  if (!result) return null;
  const ms = result.timings.compileMs + result.timings.runMs;
  return (
    <Tooltip content={t('status.calculatedHint')}>
      <span
        className={cn(
          'tabular hidden text-xs whitespace-nowrap text-text-3 transition-opacity xl:inline',
          simulating && 'opacity-50',
        )}
      >
        {t('status.calculated', { ms: ms < 10 ? ms.toFixed(1) : Math.round(ms) })}
      </span>
    </Tooltip>
  );
}

/** Opens the command palette (and shows its shortcut). */
function PaletteButton() {
  const { t } = useTranslation();
  const open = useUnit(paletteOpened);
  return (
    <button
      type="button"
      data-tour="palette"
      onClick={open}
      // From 1280 px it shows its placeholder; it is the first to give way to the project name,
      // down to the icon and shortcut.
      className="flex h-7 min-w-min shrink-[8] items-center gap-2 rounded-md whitespace-nowrap border border-border-subtle bg-bg-2 pr-1.5 pl-2.5 text-xs text-text-3 outline-none hover:border-border-strong hover:text-text-2 focus-visible:ring-2 focus-visible:ring-focus xl:w-64"
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <span className="hidden w-0 grow truncate text-left xl:inline">
        {t('palette.placeholder')}
      </span>
      <span className="sr-only xl:hidden">{t('palette.title')}</span>
      <Shortcut keys={['mod', 'K']} />
    </button>
  );
}

/** Shown while items are muted or soloed: the charts are not the saved plan. */
function WhatIf() {
  const { t } = useTranslation();
  const [active, reset] = useUnit([$hasOverrides, visibilityReset]);
  if (!active) return null;
  return (
    <Tooltip content={t('visibility.whatIfHint')}>
      <button
        type="button"
        onClick={() => {
          reset();
        }}
        className="flex h-6 items-center gap-1.5 rounded-full border border-accent bg-accent-subtle px-2.5 text-xs whitespace-nowrap text-text-1 outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
      >
        <span aria-hidden className="size-1.5 rounded-full bg-accent" />
        {t('visibility.whatIf')}
        <span className="text-text-3">· {t('visibility.reset')}</span>
      </button>
    </Tooltip>
  );
}
